import React from 'react';
import { EntitlementUsage } from '@features/entitlements';
import type { ExtendedFormDefinition } from '@features/forms';
import { useAssignForms } from '@features/forms';
import { useSubjectLanguageVariants } from '@features/user';
import { Box, Chip, Typography } from '@mui/material';

import type { Patient } from '../../types/types';
import { MessagePreview } from '../MessagePreview';

import { PatientSendPanel } from './PatientSendPanel';
import type { PreviewProps,SendData, SendResult } from './types';

interface FormReviewAdapterProps {
  open: boolean;
  onClose: () => void;
  patient?: Patient;
  patientId: string;
  selectedFormIds: string[];
  forms?: ExtendedFormDefinition[];
  onSuccess?: () => void;
}

export const FormReviewAdapter: React.FC<FormReviewAdapterProps> = ({
  open,
  onClose,
  patient,
  patientId,
  selectedFormIds,
  forms = [],
  onSuccess,
}) => {
  const { mutate: assignForms } = useAssignForms();
  const { singularCap: subjectLanguageCap } = useSubjectLanguageVariants();

  // Get selected forms
  const selectedForms = forms.filter(form => selectedFormIds.includes(form.form_id));

  const handleSend = async (request: {
    patient_id: string;
    form_ids: string[];
    channels: ('sms' | 'email')[];
  }): Promise<SendResult> => {
    return new Promise((resolve, reject) => {
      assignForms(
        request,
        {
          onSuccess: () => {
            resolve({ success: true });
          },
          onError: (error: unknown) => {
            console.error('Error sending forms:', error);
            reject(new Error(error instanceof Error ? error.message : 'Failed to send forms'));
          },
        },
      );
    });
  };

  const prepareSendRequest = (sendData: SendData) => {
    return {
      patient_id: patientId,
      form_ids: selectedFormIds,
      channels: sendData.channels,
    };
  };

  const contentRenderer = (form: ExtendedFormDefinition) => (
    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
        {form.title}
      </Typography>
      {form.type === 'public' && (
        <Chip label="Public" size="small" color="primary" variant="outlined" />
      )}
      {form.type === 'group' && (
        <Chip label="Group" size="small" color="secondary" variant="outlined" />
      )}
    </Box>
  );

  const previewRenderer = (props: PreviewProps<ExtendedFormDefinition>) => (
    <MessagePreview
      deliveryMethod={props.deliveryMethod}
      patientName={props.patientName}
      providerName={props.providerName}
      formLink="https://app.jotpsych.com/f/#####" // Placeholder
      patientPhone={patient?.phone || patient?.mobile_phone || undefined}
      customMessage={props.customMessage}
      contentType="form"
    />
  );

  return (
    <PatientSendPanel<ExtendedFormDefinition, ReturnType<typeof prepareSendRequest>>
      open={open}
      onClose={onClose}
      patientId={patientId}
      patient={patient}
      variant="slidePanel"
      contentConfig={{
        title: `Review & Send ${subjectLanguageCap} Forms`,
        itemLabel: 'Form',
        pluralLabel: 'Forms',
      }}
      content={selectedForms}
      contentRenderer={contentRenderer}
      requiredFields={['dob']}
      requiredFieldsConfig={{
        dob: {
          helpText: 'Date of birth is required for patient identity verification when accessing the form'
        }
      }}
      defaultChannels={['sms']}
      allowedChannels={['sms', 'email']}
      previewRenderer={previewRenderer}
      prepareSendRequest={prepareSendRequest}
      onSend={handleSend}
      onSuccess={onSuccess}
      footerContent={
        <EntitlementUsage capabilityName="form_sending" source="review_forms_pane" sx={{ mt: 2 }} />
      }
    />
  );
};